import React, { useEffect, useRef, useState } from "react";
import { formatBytes, MESSAGES, normaliseFile, checkAudioFile, checkVideoFile, processingFailureOf, processingFailureText, sha256Hex, trackApi, trackErrorMessage, uploadToStorage } from "./trackApi";
import { PrimaryButton, SecondaryButton, ACCENT, formatDuration } from "./shared";

// Audio file section: drag-and-drop or browse, progress, cancel, replace / remove, file name and size,
// a preview player, and the retry buttons for failed uploads and failed processing.
//
// The track has to exist (be saved) before a file can be attached, so `track` is required here; the page
// keeps the entered details, so a failure never makes the artist type them again.
export default function AudioUploader({ track, rules, disabled, onTrackChange, ensureTrack, kind, contentType = "audio" }) {
  const isVideo = contentType === "video";
  const limits = isVideo ? rules.video : rules.audio;
  const [ui, setUi] = useState({ phase: "idle", progress: 0, file: null, error: "" });
  const [dragging, setDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [removing, setRemoving] = useState(false);
  const controller = useRef(null);
  const inputRef = useRef(null);
  const busyRef = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (controller.current) controller.current.abort();
    };
  }, []);

  // Local preview of the chosen file (played before and during upload).
  useEffect(() => {
    if (!ui.file || !window.URL?.createObjectURL) {
      setPreviewUrl("");
      return undefined;
    }
    const objectUrl = URL.createObjectURL(ui.file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [ui.file]);

  const safeSet = (next) => {
    if (mounted.current) setUi((prev) => ({ ...prev, ...next }));
  };

  // Reporting the state is best-effort: the upload itself matters more than the status record.
  const report = async (id, state, message) => {
    try {
      const updated = await trackApi.uploadState({ track_id: id, state, message });
      if (mounted.current && updated) onTrackChange(updated);
    } catch (e) {
      /* ignore */
    }
  };

  const reload = async (id = track?.id) => {
    try {
      const fresh = await trackApi.detail(id);
      if (mounted.current && fresh) onTrackChange(fresh);
    } catch (e) {
      /* ignore */
    }
  };

  const runProcessing = async (retry = false, id = track?.id) => {
    safeSet({ phase: "processing", error: "" });
    try {
      const updated = await (retry ? trackApi.retry(id) : trackApi.process(id));
      if (!mounted.current) return;
      onTrackChange(updated);
      safeSet({ phase: "idle", file: null, progress: 0, error: "" });
    } catch (e) {
      const failure = processingFailureOf(e);
      // Session / network problems are not the file's fault; say so instead of "couldn't process".
      const notTheFile = !e?.response || [401, 403, 404].includes(e.response.status);
      safeSet({
        phase: "idle",
        file: null,
        progress: 0,
        processingFailure: failure,
        error: notTheFile ? trackErrorMessage(e, MESSAGES.processingFailed) : "",
      });
      await reload(id);
    }
  };

  const start = async (rawFile) => {
    if (!rawFile || busyRef.current || disabled) return;
    const file = normaliseFile(rawFile);
    const problem = isVideo ? checkVideoFile(file, rules) : checkAudioFile(file, rules);
    if (problem) {
      safeSet({ phase: "idle", file: null, progress: 0, error: problem });
      return;
    }
    busyRef.current = true;
    // The first file creates the draft, so the artist never has to "save" before uploading.
    const target = track || (ensureTrack ? await ensureTrack() : null);
    if (!target) {
      busyRef.current = false;
      return;
    }
    const ctrl = new AbortController();
    controller.current = ctrl;
    setUi({ phase: "uploading", progress: 0, file, error: "", processingFailure: null });
    await report(target.id, "uploading");
    let stored;
    let hash;
    try {
      const hashing = sha256Hex(file);
      stored = await uploadToStorage(file, { signal: ctrl.signal, onProgress: (p) => safeSet({ progress: p }) });
      hash = await hashing;
    } catch (e) {
      busyRef.current = false;
      if (e?.code === "ERR_CANCELED") {
        safeSet({ phase: "idle", progress: 0, file: null, error: "" });
        await report(target.id, "upload_pending", "Upload cancelled by the artist.");
      } else {
        safeSet({ phase: "failed", error: e?.message || MESSAGES.uploadFailed });
        await report(target.id, "upload_failed", e?.message || MESSAGES.uploadFailed);
      }
      return;
    }

    try {
      const attached = await trackApi.attachMedia({ track_id: target.id, media_type: isVideo ? "video" : "audio", ...stored, file_hash: hash });
      if (mounted.current) onTrackChange(attached);
    } catch (e) {
      busyRef.current = false;
      const message = trackErrorMessage(e);
      safeSet({ phase: "failed", error: message, file: null });
      await report(target.id, "upload_failed", message);
      return;
    }
    await runProcessing(false, target.id);
    busyRef.current = false;
  };

  // Processing started in another tab or before a page reload: keep checking until it settles.
  const serverProcessing = ui.phase === "idle" && track?.upload_status === "processing";
  useEffect(() => {
    if (!serverProcessing) return undefined;
    const timer = setInterval(reload, 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverProcessing, track?.id]);

  const cancel = () => controller.current && controller.current.abort();

  const remove = async () => {
    if (removing) return;
    setRemoving(true);
    try {
      const updated = await trackApi.removeMedia(track.id, isVideo ? "video" : "audio");
      onTrackChange(updated);
      setUi({ phase: "idle", progress: 0, file: null, error: "", processingFailure: null });
    } catch (e) {
      safeSet({ error: trackErrorMessage(e, MESSAGES[500]) });
    } finally {
      setRemoving(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    start(e.dataTransfer?.files?.[0]);
  };

  const busy = ui.phase === "uploading" || ui.phase === "processing" || serverProcessing;
  const waitingForProcessing = !busy && track?.upload_status === "uploaded";
  const uploaded = track?.audio;
  const processingFailed = !busy && track?.upload_status === "processing_failed";
  const uploadFailedOnServer = !busy && track?.upload_status === "upload_failed" && !ui.error;
  const failureText = processingFailureText(ui.processingFailure || track?.upload_error);
  const accept = limits.extensions.map((x) => `.${x}`).join(",");
  const player = previewUrl || uploaded?.url;

  return (
    <div data-testid="audio-uploader">
      {!busy && !uploaded && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`rounded-2xl border-2 border-dashed p-8 text-center transition ${
            dragging ? "bg-green-50" : "bg-gray-50"
          } ${disabled ? "opacity-60" : ""}`}
          style={{ borderColor: dragging ? ACCENT : undefined }}
        >
          <p className="font-semibold text-gray-900">Drag and drop your {isVideo ? "video" : "audio"} file here</p>
          <p className="text-sm text-gray-500 mt-1">
            {limits.extensions.map((x) => x.toUpperCase()).join(" or ")} · up to {formatBytes(limits.max_bytes)}
            {" · "}
            {(track?.kind || kind) === "clip"
              ? `clips up to ${rules.audio.max_clip_seconds} seconds`
              : `tracks up to ${Math.round(rules.audio.max_track_seconds / 60)} minutes`}
            {isVideo ? "" : ` · at least ${rules.audio.min_bitrate_kbps} kbps`}
          </p>
          {isVideo && (
            <p className="text-sm text-gray-500 mt-1">The track's sound is taken from this video for streaming and licensing; fans will see it tagged as a video.</p>
          )}
          <SecondaryButton className="mt-4" disabled={disabled} onClick={() => inputRef.current && inputRef.current.click()}>
            Browse files
          </SecondaryButton>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        data-testid="audio-input"
        aria-label={isVideo ? "Video file" : "Audio file"}
        onChange={(e) => {
          const picked = e.target.files?.[0];
          e.target.value = "";
          start(picked);
        }}
      />

      {ui.phase === "uploading" && (
        <div className="rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center justify-between gap-3">
            <p className="font-semibold text-gray-900 truncate">{ui.file?.name}</p>
            <span className="text-sm text-gray-500 shrink-0">{formatBytes(ui.file?.size)}</span>
          </div>
          <div className="h-2 rounded-full bg-gray-200 mt-3 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(ui.progress * 100)}>
            <div className="h-full transition-all" style={{ width: `${Math.round(ui.progress * 100)}%`, backgroundColor: ACCENT }} />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-sm text-gray-600">Uploading… {Math.round(ui.progress * 100)}%</span>
            <button type="button" onClick={cancel} className="text-sm font-semibold text-red-700 underline">Cancel upload</button>
          </div>
        </div>
      )}

      {(ui.phase === "processing" || serverProcessing) && (
        <div className="rounded-2xl border border-gray-200 p-4 text-gray-700" role="status">
          Processing your {isVideo ? "video" : "audio"}… checking length and {isVideo ? "sound" : "quality"}. This can take a moment.
        </div>
      )}

      {!busy && uploaded && !processingFailed && (
        <div className="rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 truncate" data-testid="audio-name">{uploaded.file_name}</p>
              <p className="text-sm text-gray-500">
                {formatBytes(uploaded.file_size)}
                {track.duration ? ` · ${formatDuration(track.duration)}` : ""}
              </p>
            </div>
            {!disabled && (
              <div className="flex gap-3 shrink-0">
                <button type="button" onClick={() => inputRef.current && inputRef.current.click()} className="text-sm font-semibold underline">Replace</button>
                <button type="button" onClick={remove} disabled={removing} className="text-sm font-semibold text-red-700 underline disabled:opacity-50">
                  {removing ? "Removing…" : "Remove"}
                </button>
              </div>
            )}
          </div>
          {player && (isVideo
            ? <video controls src={player} className="w-full mt-3 rounded-xl max-h-80 bg-black" data-testid="video-preview" />
            : <audio controls src={player} className="w-full mt-3" data-testid="audio-preview" />)}
        </div>
      )}

      {(ui.error || uploadFailedOnServer) && !busy && (
        <div className="mt-3 rounded-xl bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" role="alert">
          <p>{ui.error || MESSAGES.uploadFailed}</p>
          {ui.file && (
            <PrimaryButton className="!px-5 !py-1.5 text-sm mt-2" onClick={() => start(ui.file)}>Retry</PrimaryButton>
          )}
        </div>
      )}

      {waitingForProcessing && !ui.error && (
        <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 text-sm">
          <p>Your file is uploaded but has not been checked yet.</p>
          <PrimaryButton className="!px-5 !py-1.5 text-sm mt-2" onClick={() => runProcessing(false)} disabled={disabled}>
            Retry Processing
          </PrimaryButton>
        </div>
      )}

      {processingFailed && (
        <div className="mt-3 rounded-xl bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" role="alert">
          <p>{failureText}</p>
          <div className="flex flex-wrap gap-2 mt-2">
            <PrimaryButton className="!px-5 !py-1.5 text-sm" onClick={() => runProcessing(true)} disabled={disabled}>
              Retry Processing
            </PrimaryButton>
            <SecondaryButton className="!px-5 !py-1.5 text-sm" onClick={() => inputRef.current && inputRef.current.click()} disabled={disabled}>
              Upload a different file
            </SecondaryButton>
          </div>
        </div>
      )}
    </div>
  );
}
